import PageType from '@interfaces/page'
import { useRouter } from 'next/router'
import ErrorPage from 'next/error'
import Layout from '@comp/layout'
import markdownToHtml from '@lib/markdownToHtml'
import markdownStyles from '@style/LegalPageMarkdownStyles.module.css'
import styles from '@style/Hero.module.css'
import { motion } from 'framer-motion'
import { getPages, getPageBySlug } from '@lib/api'
import FinalCTASection from '@comp/final-cta-section'
import Head from 'next/head'

type Props = {
  post: PageType
  morePosts: PageType[]
  preview?: boolean
}

export default function LegalPage({ post }: Props) {
  const router = useRouter()
  const title = `Monofor – ${post.title}`
  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />
  }
  return (
    <Layout>
      {router.isFallback ? (
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-tight md:leading-none mb-12 text-center md:text-left">
          Loading…
        </h1>
      ) : (
        <>
          <Head>
            <title>{title}</title>
            <meta property="og:type" content="article" />
            <meta property="og:title" content={title} />
            {/* <meta name="twitter:card" content="summary_large_image" /> */}
            <meta name="twitter:title" content={title} />
            <meta property="og:site_name" content="Monofor" />
            <meta property="article:published_time" content={post.date} />
          </Head>
          <section
            className={`${styles.heroContainer} ${styles.company}`}
            style={{ backgroundColor: '#CDE7D8' }}
          >
            <div className={styles.heroContent}>
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 0.8 }}
              >
                {post.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 0.8, delay: 0.8 }}
              >
                {post.excerpt}
              </motion.p>
            </div>
            <div className={styles.heroImageWithoutBg}>
              <img
                src={post.coverImage}
                alt={post.title}
                style={{ objectFit: 'contain', width: '100%', height: '100%' }}
              />
            </div>
          </section>
          <article className={markdownStyles.article}>
            <div className={markdownStyles.markdownBody}>
              <div
                className={markdownStyles['markdown']}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>
          </article>
          <FinalCTASection message={post.callToAction} />
        </>
      )}
    </Layout>
  )
}

type Params = {
  params: {
    slug: string
  }
}

export async function getStaticProps({ params }: Params) {
  const post = getPageBySlug('_pages/features', params.slug, [
    'title',
    'excerpt',
    'date',
    'slug',
    'content',
    'ogImage',
    'coverImage',
    'callToAction',
  ])

  if (!post) {
    return {
      props: {},
    }
  }

  const content = await markdownToHtml(post.content || '')

  return {
    props: {
      post: {
        ...post,
        content,
      },
    },
  }
}

export async function getStaticPaths() {
  const posts = getPages('_pages/features', ['slug'])
  const paths = posts
    .map((post) => {
      return {
        params: {
          slug: post.slug,
        },
      }
    })
    .filter((x) => x.params.slug)
  return {
    paths: paths,
    fallback: false,
  }
}
